import { useState, useEffect, useCallback } from "react";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { IoTrashOutline } from "react-icons/io5";
import Modal from "@modalSrc/Modal";
import ConfirmModal from "@modals/ConfirmModal";
import { toast } from "react-toastify";
import RightTimeApi from "@/API";
import Enums from "@/enums";
import Helpers from "@/Helpers";
import DateTimePicker from "@elements/DateTimePicker";

type FormErrors = Record<string, string | null>;

export default function PaymentEditModal(props) {
    const { paymentId, companyId, invoiceId, defaultAmount, onClose, updateSpinner } = props;
    const [errors, setErrors] = useState<FormErrors>({});
    const [paymentData, setPaymentData] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [filteredInvoices, setFilteredInvoices] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [isValid, setIsValid] = useState(false);
    const [allocations, setAllocations] = useState([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showRefundConfirm, setShowRefundConfirm] = useState(false);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);

    const isOpen = paymentId !== null && paymentId !== undefined;

    // Define functions first with useCallback to prevent infinite re-renders
    const resetForm = useCallback(() => {
        setErrors({});
        setIsValid(false); // Reset validation state
        setPaymentData({
            companyId: companyId || 0,
            paymentMethodId: Enums.PAYMENT_METHOD.Check,
            amount: defaultAmount || null,
            paymentMethodReference: '',
            paymentDate: new Date().toISOString(),
            allocations: []
        });
    }, [companyId, defaultAmount]);

    const loadCompaniesWithPendingInvoicesAsync = useCallback(async () => {
        updateSpinner(true);
        try {
            const resp = await RightTimeApi.getAsync("/Companies/PendingInvoices");
            if (resp && resp.ok && resp.jsonData) {
                setCompanies(resp.jsonData);
                let selectedCompanyId = companyId || 0;
                if(!selectedCompanyId && resp.jsonData.length===1) 
                    selectedCompanyId = resp.jsonData[0].id;
                setPaymentData(prev => ({
                    ...prev,
                    companyId: selectedCompanyId
                }));
            } else {
                toast.error("Failed to load companies with pending invoices");
                console.error("Failed to load companies with pending invoices:", resp.status, resp.statusText);
            }
        } catch (error) {
            console.error("Error loading companies with pending invoices:", error);
            toast.error("Error loading companies with pending invoices");
        } finally {
            updateSpinner(false);
        }
    }, [updateSpinner, companyId]);

    const loadPaymentDataAsync = useCallback(async () => {
        if (!paymentId || paymentId === 0) return;

        updateSpinner(true);
        try {
            const resp = await RightTimeApi.getAsync(`/Payment/${paymentId}`);
            if (resp && resp.ok && resp.jsonData) {
                setInvoices(resp.jsonData.invoices);
                setCompanies(resp.jsonData.companies || []);
                let payment = resp.jsonData.payment;
                // if(resp.jsonData.companies.length===1)
                setPaymentData(payment);
            } else {
                toast.error("Failed to load payment data");
                console.error("Failed to load payment data:", resp.status, resp.statusText);
            }
        } catch (error) {
            console.error("Error loading payment data:", error);
            toast.error("Error loading payment data");
        } finally {
            updateSpinner(false);
        }
    }, [paymentId, updateSpinner]);

    const loadPendingInvoicesAsync = useCallback(async () => {
        if (!paymentData?.companyId) return;

        updateSpinner(true);
        try {
            const resp = await RightTimeApi.getAsync(`/Company/${paymentData.companyId}/PendingInvoices`);
            if (resp && resp.ok && resp.jsonData) {
                setInvoices(resp.jsonData);
                // Initially show all invoices sorted chronologically
                setFilteredInvoices(sortInvoicesChronologically(resp.jsonData));
            } else {
                toast.error("Failed to load pending invoices");
                console.error("Failed to load pending invoices:", resp.status, resp.statusText);
            }
        } catch (error) {
            console.error("Error loading pending invoices:", error);
            toast.error("Error loading pending invoices");
        } finally {
            updateSpinner(false);
        }
    }, [paymentData?.companyId, updateSpinner]);

    // Helper function to sort invoices chronologically (oldest first)
    const sortInvoicesChronologically = useCallback((invoiceList) => {
        return [...invoiceList].sort((a, b) => {
            const dateA = new Date(a.invoiceDate || a.createdDate || 0);
            const dateB = new Date(b.invoiceDate || b.createdDate || 0);
            return dateA.getTime() - dateB.getTime(); // Oldest first
        });
    }, []);

    const filterInvoicesByDate = useCallback((paymentDate) => {
        if (!paymentDate || !invoices.length) {
            setFilteredInvoices(sortInvoicesChronologically(invoices));
            return;
        }

        const filterDate = new Date(paymentDate);
        const filtered = invoices.filter(invoice => {
            const invoiceDate = new Date(invoice.invoiceDate || invoice.createdDate || 0);
            return invoiceDate <= filterDate;
        });

        setFilteredInvoices(sortInvoicesChronologically(filtered));
    }, [invoices, sortInvoicesChronologically]);

    const handlePaymentDateChange = useCallback((e) => {
        const paymentDate = e?.target?.value || "";
        setPaymentData(prev => ({
            ...prev,
            paymentDate
        }));

        if (paymentDate) {
            filterInvoicesByDate(paymentDate);
        }

        setErrors(prev => (prev.paymentDate ? { ...prev, paymentDate: null } : prev));
    }, [filterInvoicesByDate]);

    // Add useEffects after function definitions
    useEffect(() => {
        if (isOpen) {
            setErrors({});

            if (paymentId === 0) {
                // Initialize form for new payment
                resetForm();
                // Load companies with pending invoices if no specific company
                loadCompaniesWithPendingInvoicesAsync();
            } else {
                // Load existing payment data for editing
                loadPaymentDataAsync();
            }
        }
    }, [paymentId, isOpen, resetForm, loadCompaniesWithPendingInvoicesAsync, loadPaymentDataAsync]);

    useEffect(() => {
        // Only load pending invoices for new payments (paymentId === 0)
        // For existing payments, we already have the correct invoice data from loadPaymentDataAsync
        if(paymentData?.companyId > 0 && paymentId === 0) {
            loadPendingInvoicesAsync();
        }
    }, [paymentData?.companyId, loadPendingInvoicesAsync, paymentId]);

    // Auto-select company when there's only one option
    useEffect(() => {
        if (companies.length === 1 && !paymentData?.companyId) {
            setPaymentData(prev => ({
                ...prev,
                companyId: companies[0].id
            }));
        }
    }, [companies, paymentData?.companyId]);

    // Filter invoices when invoices or payment date changes
    useEffect(() => {
        if (paymentData?.paymentDate) {
            filterInvoicesByDate(paymentData.paymentDate);
        } else {
            setFilteredInvoices(sortInvoicesChronologically(invoices));
        }
    }, [invoices, paymentData?.paymentDate, filterInvoicesByDate]);

    // Auto-allocate payment when defaultAmount is set for new payments
    useEffect(() => {
        // Only auto-allocate if this is a new payment and we have payment data with amount and filtered invoices
        if (paymentId === 0 && paymentData?.amount && defaultAmount && filteredInvoices.length > 0) {
            // Small delay to ensure all data is loaded
            const timeoutId = setTimeout(() => {
                if (invoiceId) {
                    // Allocate specifically to the provided invoice
                    allocateToSpecificInvoice(invoiceId, paymentData.amount);
                } else {
                    // General auto-allocation across all invoices
                    autoAllocatePaymentAmount();
                }
            }, 100);
            return () => clearTimeout(timeoutId);
        }
    }, [paymentId, paymentData?.amount, defaultAmount, filteredInvoices.length, invoiceId]);

    // Remove automatic validation - only validate when user tries to save

    const onUserInput = useCallback((e) => {
        let value = e.target.value;

        switch (e.target.dataset.fieldType) {
            case "number": value = parseInt(e.target.value); break;
            case "float": value = parseFloat(e.target.value.replace(/,/g, '')); break;
        }
        setPaymentData(prev => ({
            ...prev,
            [e.target.dataset.fieldName]: value
        }));
    }, []);

    function getPaymentReferenceLabel() {
        if (!paymentData) return "Reference";
        if (paymentData.paymentMethodId === Enums.PAYMENT_METHOD.Check) return "Check Number";
        if (paymentData.paymentMethodId === Enums.PAYMENT_METHOD.BankTransfer) return "Transfer Reference ID";
        if (paymentData.paymentMethodId === Enums.PAYMENT_METHOD.Other) return "Details";
        return "Reference";
    }

    function shouldShowPaymentReference() {
        if (!paymentData) return false;
        return paymentData.paymentMethodId === Enums.PAYMENT_METHOD.Check ||
            paymentData.paymentMethodId === Enums.PAYMENT_METHOD.BankTransfer ||
            paymentData.paymentMethodId === Enums.PAYMENT_METHOD.Other;
    }

    function validateNumericInput(value) {
        // Allow only numbers, commas, and periods
        const numericRegex = /^[0-9,.\s]*$/;
        return numericRegex.test(value);
    }

    function handleAppliedAmountBlur(invoiceId, value) {
        if (value && !validateNumericInput(value)) {
            // Clear invalid input and show error by setting the allocation amount to 0
            setPaymentData(prev => {
                const currentAllocations = prev?.payment?.allocations || [];
                const updatedAllocations = currentAllocations.map(allocation => {
                    if (allocation.invoiceId === invoiceId) {
                        return {
                            ...allocation,
                            amount: 0
                        };
                    }
                    return allocation;
                });

                return {
                    ...prev,
                    payment: {
                        ...prev.payment,
                        allocations: updatedAllocations
                    }
                };
            });
            // You could also add error handling here if needed
        }
    }

    // Auto-allocate payment amount to oldest invoices first
    function autoAllocatePaymentAmount() {
        if (!paymentData?.amount || paymentData.amount <= 0 || !filteredInvoices.length) {
            return;
        }

        const paymentAmount = parseFloat(paymentData.amount) || 0;
        
        // Sort filtered invoices by date (oldest first) - assuming invoiceDate exists
        const sortedInvoices = [...filteredInvoices].sort((a, b) => {
            const dateA = new Date(a.invoiceDate || a.createdDate || 0);
            const dateB = new Date(b.invoiceDate || b.createdDate || 0);
            return dateA.getTime() - dateB.getTime();
        });

        let remainingAmount = paymentAmount;
        const newAllocations = [];

        // Allocate to invoices starting with oldest
        for (const invoice of sortedInvoices) {
            if (remainingAmount <= 0) break;

            const balanceDue = parseFloat(invoice.balanceDue || invoice.totalAmount || 0);
            if (balanceDue <= 0) continue; // Skip fully paid invoices

            // Apply the lesser of remaining amount or balance due
            const amountToApply = Math.min(remainingAmount, balanceDue);
            
            if (amountToApply > 0) {
                newAllocations.push({
                    id: 0, // Will be set by server
                    amount: amountToApply,
                    statusId: 1, // Assuming 1 is active/applied status
                    invoiceId: invoice.id
                });

                remainingAmount -= amountToApply;
            }
        }

        // Update payment data with new allocations
        setPaymentData(prev => ({
            ...prev,
            allocations: newAllocations
        }));
    }

    // Allocate payment amount to a specific invoice
    function allocateToSpecificInvoice(targetInvoiceId, amount) {
        if (!targetInvoiceId || !amount || amount <= 0) {
            return;
        }

        const targetInvoice = filteredInvoices.find(inv => inv.id === targetInvoiceId);
        if (!targetInvoice) {
            return;
        }

        const balanceDue = parseFloat(targetInvoice.balanceDue || targetInvoice.totalAmount || 0);
        const amountToApply = Math.min(parseFloat(amount), balanceDue);
        
        if (amountToApply > 0) {
            const newAllocations = [{
                id: 0, // Will be set by server
                amount: amountToApply,
                statusId: 1, // Assuming 1 is active/applied status
                invoiceId: targetInvoiceId
            }];

            // Update payment data with the specific allocation
            setPaymentData(prev => ({
                ...prev,
                allocations: newAllocations
            }));
        }
    }

    function handlePaymentAmountBlur(e) {
        // Only auto-allocate if this is a new payment (not editing existing)
        if (paymentId === 0) {
            autoAllocatePaymentAmount();
        }
    }

    function toggleInvoiceSelection(invoiceId) {
        setPaymentData(prev => {
            const currentAllocations = prev?.payment?.allocations || [];
            const existingAllocation = currentAllocations.find(allocation => allocation.invoiceId === invoiceId);

            if (existingAllocation) {
                // Remove allocation when unchecking
                const newAllocations = currentAllocations.filter(allocation => allocation.invoiceId !== invoiceId);
                return {
                    ...prev,
                    payment: {
                        ...prev.payment,
                        allocations: newAllocations
                    }
                };
            } else {
                // Add allocation when checking
                const totalPayment = prev?.payment?.amount || 0;

                // Calculate how much is already applied to other invoices
                const totalApplied = currentAllocations.reduce((sum, allocation) => {
                    return sum + (allocation.amount || 0);
                }, 0);

                // Calculate remaining payment amount
                const remainingPayment = totalPayment - totalApplied;

                if (remainingPayment > 0) {
                    // Find the invoice being checked to get its amount owed
                    const invoice = invoices.find(inv => inv.id === invoiceId);

                    // Apply the lesser of: remaining payment or amount owed on invoice
                    const amountToApply = Math.min(remainingPayment, invoice.totalAmount, 0);

                    // Create new allocation
                    const newAllocation = {
                        id: 0, // Will be set by server
                        amount: amountToApply,
                        statusId: 1, // Assuming 1 is active/applied status
                        invoiceId: invoiceId
                    };
                    setAllocations(prev => [...prev, newAllocation]);
                }
            }
        });
    }

    function getUnappliedAmount() {
        const paymentAmount = parseFloat(paymentData?.amount) || 0;
        return Math.max(0, paymentAmount - getTotalAppliedAmount());
    }

    function isRefundedPayment() {
        return paymentData?.statusId === Enums.PAYMENT_STATUS.Refunded;
    }

    function getPaymentStatusLabel() {
        switch (paymentData?.statusId) {
            case Enums.PAYMENT_STATUS.Refunded:
                return "Refunded";
            case Enums.PAYMENT_STATUS.PartiallyPaid:
                return "Partially allocated";
            case Enums.PAYMENT_STATUS.Failed:
                return "Failed";
            case Enums.PAYMENT_STATUS.Pending:
                return "Pending";
            default:
                return "Completed";
        }
    }

    function getTotalAppliedAmount() {
        const allocations = paymentData?.allocations || [];
        return allocations.reduce((sum, allocation) => {
            return sum + (parseFloat(allocation.amount) || 0);
        }, 0);
    }

    function validateForm() {
        if (!paymentData) return false;

        const newErrors: FormErrors = {};

        if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
            newErrors.amount = 'Payment amount must be greater than 0';
        }

        if (!paymentData.paymentDate) {
            newErrors.paymentDate = 'Payment date is required';
        }

        if (!paymentData.paymentMethodId) {
            newErrors.paymentMethod = 'Payment method is required';
        }

        // Validate company selection when no specific companyId is provided as prop and it's a new payment
        if (!companyId && !paymentData?.companyId) {
            newErrors.selectedCompany = 'Please select a company';
        }

        // Validate allocations if any invoices are selected
        const allocations = paymentData?.allocations || [];
        if (allocations.length > 0) {
            const totalAllocated = allocations.reduce((sum, allocation) => {
                return sum + (allocation.amount || 0);
            }, 0);

            const paymentAmount = paymentData?.amount || 0;

            if (totalAllocated > paymentAmount) {
                newErrors.allocation = 'Total allocated amount cannot exceed payment amount';
            }

            // Check if any allocation has no applied amount
            const hasInvalidAllocations = allocations.some(allocation => {
                return (allocation.amount || 0) <= 0;
            });

            if (hasInvalidAllocations) {
                newErrors.allocation = 'All selected invoices must have a valid applied amount greater than 0';
            }

            // Check if any allocation exceeds the balance due
            const hasExcessiveAllocations = allocations.some(allocation => {
                const invoice = invoices.find(inv => inv.id === allocation.invoiceId);
                if (invoice) {
                    const balanceDue = invoice.balanceDue || invoice.totalAmount;
                    return (allocation.amount || 0) > balanceDue;
                }
                return false;
            });

            if (hasExcessiveAllocations) {
                const excessiveAllocation = allocations.find(allocation => {
                    const invoice = invoices.find(inv => inv.id === allocation.invoiceId);
                    if (invoice) {
                        const balanceDue = invoice.balanceDue || invoice.totalAmount;
                        return (allocation.amount || 0) > balanceDue;
                    }
                    return false;
                });
                const invoice = invoices.find(inv => inv.id === excessiveAllocation.invoiceId);
                const balanceDue = invoice.balanceDue || invoice.totalAmount;
                newErrors.allocation = `Applied amount cannot exceed balance due of ${Helpers.formatCurrency(balanceDue)} for invoice ${invoice.invoiceNumber || invoice.id}`;
            }
        }

        setErrors(newErrors);
        const valid = Object.keys(newErrors).length === 0;
        setIsValid(valid);
        return valid;
    }

    async function handleSave() {
        // Clear any existing errors before validation
        setErrors({});

        if (validateForm()) {
            try {
                updateSpinner(true);

                // Create request body with payment data and allocations
                const requestBody = {
                    id: paymentId ?? 0,
                    amount: parseFloat(paymentData.amount),
                    paymentDate: Helpers.formatDateOnly(paymentData.paymentDate),
                    paymentMethodId: parseInt(paymentData.paymentMethodId),
                    paymentReference: paymentData.paymentReference,
                    allocations: paymentData.allocations || []
                };

                // Use the appropriate company ID - either passed in or user-selected

                const resp = await RightTimeApi.sendAsync(`/Company/${paymentData.companyId}/Payment`, requestBody);
                if (resp && resp.ok) {
                    toast.success("Payment recorded successfully");
                    onClose();
                    // Optionally refresh the pending invoices or parent component
                } else {
                    toast.error("Failed to record payment");
                    console.error("Failed to record payment:", resp.status, resp.statusText);
                }
            } catch (error) {
                console.error("Error recording payment:", error);
                toast.error("Error recording payment");
            } finally {
                updateSpinner(false);
            }
        }
    }

    async function refundPaymentAsync() {
        if (!paymentId || paymentId === 0) {
            toast.error("Cannot refund a new payment");
            return;
        }

        try {
            updateSpinner(true);
            const resp = await RightTimeApi.postAsync(`/Payment/${paymentId}/Refund`, {});
            if (resp && resp.ok) {
                toast.success("Payment refunded successfully");
                setShowRefundConfirm(false);
                onClose();
            } else {
                toast.error(resp?.message || "Failed to refund payment");
            }
        } catch (error) {
            console.error("Error refunding payment:", error);
            toast.error("Error refunding payment");
        } finally {
            updateSpinner(false);
        }
    }

    async function deletePaymentAsync() {
        if (!paymentId || paymentId === 0) {
            toast.error("Cannot delete a new payment");
            return;
        }

        try {
            updateSpinner(true);
            const resp = await RightTimeApi.deleteAsync(`/Payment/${paymentId}`);
            if (resp && resp.ok) {
                toast.success("Payment deleted successfully");
                setShowDeleteConfirm(false);
                onClose();
            } else {
                toast.error("Failed to delete payment");
                console.error("Failed to delete payment:", resp.status, resp.statusText);
            }
        } catch (error) {
            console.error("Error deleting payment:", error);
            toast.error("Error deleting payment");
        } finally {
            updateSpinner(false);
        }
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSave();
        } else if (e.key === 'Escape') {
            onClose();
        }
    }

    function getPaymentMethodText(methodId) {
        const methods = {
            [Enums.PAYMENT_METHOD.CreditCard]: 'Credit Card',
            [Enums.PAYMENT_METHOD.BankTransfer]: 'Bank Transfer',
            [Enums.PAYMENT_METHOD.Cash]: 'Cash',
            [Enums.PAYMENT_METHOD.Check]: 'Check',
            [Enums.PAYMENT_METHOD.Other]: 'Other'
        };
        return methods[methodId] || 'Unknown';
    }

    if (!isOpen) return null;

    const footer = (
        <>
            <div className="footerLeft">
            </div>
            <div className="footerRight">
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onClose}
                >
                    Cancel
                </button>
                {paymentId !== 0 && !isRefundedPayment() && (
                    <button
                        type="button"
                        className="btn btn-warning"
                        onClick={() => setShowRefundConfirm(true)}
                        title="Refund Payment"
                    >
                        Refund
                    </button>
                )}
                {paymentId !== 0 && (
                    <button
                        type="button"
                        className="btn btn-danger iconButton"
                        onClick={() => setShowDeleteConfirm(true)}
                        title="Delete Payment"
                        disabled={isRefundedPayment()}
                    >
                        <IoTrashOutline />
                    </button>
                )}
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={isRefundedPayment()}
                >
                    {paymentData && paymentData?.id > 0 ? "Update Payment" : "Record Payment"}
                </button>
            </div>
        </>
    );

    return (
        <>
            <Modal
                isOpen={isOpen}
                title={`Payment   ${paymentId && paymentId > 0 ? ` (${paymentId})` : ''}`}
                onClose={onClose}
                footer={footer}
                className="paymentEditModal"
                size="lg"
            >
                <div className="modalBody">
                    <div className="formSection">
                        {/* Show company selection when companies are available */}
                        {companies.length > 0 ? (
                            <>
                                <div className="formGroup">
                                    <label htmlFor="selectedCompany">Company *</label>
                                    <select
                                        id="selectedCompany"
                                        value={paymentData?.companyId || ''}
                                        data-field-name="companyId"
                                        data-field-type="number"
                                        onChange={onUserInput}
                                        className={errors.selectedCompany ? 'error' : ''}
                                    >
                                        {companies.length > 1 && <option value="">Select a company...</option>}
                                        {companies.map((company) => (
                                            <option key={company.id} value={company.id}>
                                                {company.name}
                                            </option>
                                        ))}
                                    </select>
                                    {/* {errors.selectedCompany && <span className="errorText">{errors.selectedCompany}</span>} */}
                                </div>
                            </>
                        ) : (
                            <div className="center">Loading companies...</div>
                        )}
                    </div>
                    {(paymentData?.companyId > 0 || companyId > 0) && (
                        <>
                            <div className="formSection">
                                <div className="formGroup">
                                    <label htmlFor="paymentDate">Payment Date *</label>
                                    <DateTimePicker
                                        field="paymentDate"
                                        pickerTitle="Payment Date"
                                        id="paymentDate"
                                        value={paymentData?.paymentDate ? paymentData.paymentDate.split('T')[0] : ''}
                                        onChange={handlePaymentDateChange}
                                        dateOnly
                                        className={errors.paymentDate ? 'error' : ''}
                                        autoFocus
                                    />
                                    {errors.paymentDate && <span className="errorText">{errors.paymentDate}</span>}
                                </div>

                                <div className="formGroup">
                                    <label htmlFor="amount">Amount *</label>
                                    <input
                                        id="amount"
                                        type="text"
                                        data-field-name="amount"
                                        data-field-type="float"
                                        value={paymentData?.amount || ''}
                                        onChange={onUserInput}
                                        onBlur={handlePaymentAmountBlur}
                                        onKeyDown={handleKeyDown}
                                        className={errors.amount ? 'error' : ''}
                                    />
                                    {errors.amount && <span className="errorText">{errors.amount}</span>}
                                </div>

                                <div className="formGroup">
                                    <label htmlFor="paymentMethod">Payment Method *</label>
                                    <select
                                        id="paymentMethod"
                                        value={paymentData?.paymentMethodId || Enums.PAYMENT_METHOD.Check}
                                        data-field-name="paymentMethodId"
                                        onChange={onUserInput}
                                        className={errors.paymentMethod ? 'error' : ''}
                                    >
                                        <option value={Enums.PAYMENT_METHOD.Check}>Check</option>
                                        <option value={Enums.PAYMENT_METHOD.CreditCard}>Credit Card</option>
                                        <option value={Enums.PAYMENT_METHOD.BankTransfer}>Bank Transfer</option>
                                        <option value={Enums.PAYMENT_METHOD.Cash}>Cash</option>
                                        <option value={Enums.PAYMENT_METHOD.Other}>Other</option>
                                    </select>
                                    {errors.paymentMethod && <span className="errorText">{errors.paymentMethod}</span>}
                                </div>

                                {shouldShowPaymentReference() && (
                                    <div className="formGroup">
                                        <label htmlFor="paymentReference">{getPaymentReferenceLabel()}</label>
                                        <input
                                            type="text"
                                            id="paymentReference"
                                            data-field-name="paymentReference"
                                            data-field-type="string"
                                            value={paymentData?.paymentReference || ''}
                                            onChange={onUserInput}
                                            placeholder={`Enter ${getPaymentReferenceLabel().toLowerCase()}`}
                                        />
                                    </div>
                                )}

                                {errors.allocation && (
                                    <div className="errorText allocationError">
                                        {errors.allocation}
                                    </div>
                                )}

                                {paymentData?.amount > 0 && (
                                    <div className="paymentAllocationSummary">
                                        <span>Applied: {Helpers.formatCurrency(getTotalAppliedAmount())}</span>
                                        <span>Unapplied: {Helpers.formatCurrency(getUnappliedAmount())}</span>
                                        {paymentData?.statusId && (
                                            <span>Status: {getPaymentStatusLabel()}</span>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="formSection">

                                <div className="tableWrapper factTable scrollable">
                                    <table>
                                        <caption>
                                          <span className="title">Pending Invoices</span>
                                        </caption>
                                        <thead>
                                            <tr>
                                                <th>Invoice #</th>
                                                <th>Date</th>
                                                <th>Total</th>
                                                <th>Balance Due</th>
                                                <th>Applied $</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredInvoices.length > 0 ? (
                                                filteredInvoices.map((invoice) => (
                                                    <tr key={invoice.id}>
                                                        <td>{invoice.invoiceNumber || invoice.id}</td>
                                                        <td>{Helpers.formatDate(invoice.invoiceDate)}</td>
                                                        <td className="right">{Helpers.formatCurrency(invoice.totalAmount)}</td>
                                                        <td className="right">{Helpers.formatCurrency(invoice.balanceDue || invoice.totalAmount)}</td>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                value={paymentData?.allocations?.find(allocation => allocation.invoiceId === invoice.id)?.amount || ''}
                                                                onChange={(e) => {
                                                                    const value = e.target.value;
                                                                    const numericValue = parseFloat(value) || 0;

                                                                    setPaymentData(prev => {
                                                                        const currentAllocations = prev?.allocations || [];
                                                                        const existingAllocation = currentAllocations.find(allocation => allocation.invoiceId === invoice.id);

                                                                        if (numericValue > 0) {
                                                                            // Create or update allocation
                                                                            if (existingAllocation) {
                                                                                // Update existing allocation
                                                                                const updatedAllocations = currentAllocations.map(allocation => {
                                                                                    if (allocation.invoiceId === invoice.id) {
                                                                                        return { ...allocation, amount: numericValue };
                                                                                    }
                                                                                    return allocation;
                                                                                });
                                                                                return { ...prev, allocations: updatedAllocations };
                                                                            } else {
                                                                                // Create new allocation
                                                                                const newAllocation = {
                                                                                    id: 0,
                                                                                    amount: numericValue,
                                                                                    statusId: 1,
                                                                                    invoiceId: invoice.id
                                                                                };
                                                                                return { ...prev, allocations: [...currentAllocations, newAllocation] };
                                                                            }
                                                                        } else {
                                                                            // Remove allocation if value is 0 or empty
                                                                            const updatedAllocations = currentAllocations.filter(allocation => allocation.invoiceId !== invoice.id);
                                                                            return { ...prev, allocations: updatedAllocations };
                                                                        }
                                                                    });
                                                                }}
                                                                style={{ width: '80px', textAlign: 'right' }}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={7} className="center">
                                                        {paymentData?.companyId ? 
                                                            (invoices.length > 0 && filteredInvoices.length === 0 ? 
                                                                "No pending invoices found before the selected payment date" : 
                                                                "No pending invoices found") : 
                                                            "Select a company to view pending invoices"}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </Modal>

            <ConfirmModal
                isOpen={showRefundConfirm}
                onConfirm={refundPaymentAsync}
                onCancel={() => setShowRefundConfirm(false)}
                title="Refund Payment"
                message="Refund this payment and reverse all invoice allocations?"
                confirmText="Yes, Refund Payment"
                dangerText="The payment record will be kept with Refunded status. Invoice balances will be recalculated."
            />

            <ConfirmModal
                isOpen={showDeleteConfirm}
                onConfirm={deletePaymentAsync}
                onCancel={() => setShowDeleteConfirm(false)}
                title="Delete Payment"
                message="Are you sure you want to delete this payment? This action cannot be undone."
                confirmText="Yes, Delete Payment"
                dangerText="This will permanently remove the payment and all associated allocations."
            />
        </>
    );
}

